import { CheckCircle2, Copy, Loader2, RotateCcw, XCircle } from "lucide-react";
import { mediaUrl } from "@/lib/api";
import { useUploads } from "@/context/UploaderContext";

const ICON = {
  imported: <CheckCircle2 size={16} className="text-emerald-600" />,
  duplicate: <Copy size={16} className="text-amber-600" />,
  failed: <XCircle size={16} className="text-red-600" />,
};
const DONE_STATES = new Set(["imported", "duplicate", "failed", "processing"]);
const LABEL = { queued: "Waiting", uploading: "Uploading", processing: "Processing", imported: "Imported", duplicate: "Duplicate hidden", failed: "Failed" };

// "Recent imports" entry for files uploaded straight from this device (this session)
export const DeviceUploadsCard = () => {
  const { entries, summary, retry, retryFailed, clearFinished } = useUploads();
  if (!entries.length) return null;
  const pending = (summary.uploading || 0) + (summary.processing || 0) + (summary.queued || 0);
  const overall = Math.round(entries.reduce((n, e) => n + (e.state === "failed" ? 0 : e.progress), 0) / entries.length);
  const albums = Object.entries(entries.reduce((acc, e) => {
    if (!e.group) return acc;
    const a = acc[e.group] || { done: 0, total: 0, failed: 0 };
    return { ...acc, [e.group]: { total: a.total + 1, done: a.done + (DONE_STATES.has(e.state) ? 1 : 0), failed: a.failed + (e.state === "failed" ? 1 : 0) } };
  }, {}));
  return (
    <section className="import-card" data-testid="device-uploads-card">
      <div className="flex flex-wrap items-center gap-3">
        <h2 className="serif text-2xl text-neutral-900 mr-auto">From this device</h2>
        <span className={`status-pill ${pending ? "processing" : "done"}`} data-testid="device-uploads-status">{pending ? "uploading" : "done"}</span>
        {!pending && <button className="btn-outline !py-1.5 !px-3 text-xs" onClick={clearFinished} data-testid="upload-clear-button">Clear</button>}
      </div>
      <div className="upload-summary" data-testid="upload-summary">
        <span data-testid="upload-summary-imported-count">{summary.imported || 0} imported</span>
        <span data-testid="upload-summary-duplicates-count">{summary.duplicate || 0} duplicates hidden</span>
        <span data-testid="upload-summary-failed-count">{summary.failed || 0} failed</span>
        {pending > 0 && <span data-testid="upload-summary-pending-count">{pending} in progress</span>}
        {summary.failed > 0 && <button className="underline text-red-700" onClick={retryFailed} data-testid="upload-retry-failed-button">Retry failed</button>}
      </div>
      <div className="h-1.5 bg-neutral-200 mt-3 overflow-hidden" data-testid="upload-overall-progress"><div className="h-full bg-neutral-900 transition-[width] duration-500" style={{ width: `${overall}%` }} /></div>
      {albums.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3" data-testid="upload-album-counts">
          {albums.map(([name, a], i) => (
            <span key={name} className="status-pill done" data-testid={`upload-album-count-${i}`}>{name} · {a.done}/{a.total}{a.failed ? ` · ${a.failed} failed` : ""}</span>
          ))}
        </div>
      )}
      <ul className="mt-3 space-y-2 max-h-80 overflow-y-auto">
        {entries.map((e, i) => (
          <li key={e.key} className="upload-row" data-testid={`upload-progress-item-${i}`}>
            <span className="upload-thumb">{e.thumb ? <img src={mediaUrl(e.thumb)} alt="" /> : ICON[e.state] || <Loader2 size={16} className="animate-spin" />}</span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-neutral-800">{e.name}</div>
              <div className="h-1 bg-neutral-200 rounded mt-1 overflow-hidden"><div className="h-full bg-neutral-800 transition-[width] duration-300" style={{ width: `${e.progress}%` }} /></div>
              <div className="text-xs text-neutral-500 mt-1" data-testid={`upload-progress-state-${i}`}>
                {LABEL[e.state]}{e.state === "uploading" ? ` ${Math.round(e.progress)}%` : ""}{e.error ? ` — ${e.error}` : ""}
              </div>
            </div>
            {e.state === "failed" && <button className="icon-btn !text-neutral-700 hover:!bg-neutral-200" onClick={() => retry(e.key)} aria-label="Retry" data-testid={`upload-retry-${i}`}><RotateCcw size={15} /></button>}
          </li>
        ))}
      </ul>
    </section>
  );
};
